import { useEffect, useRef, useState } from "react"
import { ArrowRight, Pause, Play } from "lucide-react"
import { vizag2026 as event } from "../data/vizag2026"
import { availableAsset } from "./EventMedia"
import { VizagLogo } from "./VizagLogo"

export function VizagHero({ onPresentationsClick, onAgendaClick }) {
  const videoRef = useRef(null)
  const video = availableAsset(event.heroVideo)
  const image = availableAsset(event.heroImage) || availableAsset(event.heroImageAlternative)
  const [videoFailed, setVideoFailed] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    if (videoRef.current && video && !videoFailed) {
      videoRef.current.play().catch(() => setPlaying(false))
    }
  }, [video, videoFailed])

  const toggleVideo = () => {
    if (!videoRef.current) return
    if (playing) videoRef.current.pause()
    else videoRef.current.play().catch(() => setPlaying(false))
  }

  return (
    <section className="relative isolate overflow-hidden bg-green-900 text-white">
      <img src={image && !imageFailed ? image : event.heroFallback} alt="" onError={() => setImageFailed(true)} className="absolute inset-0 -z-20 h-full w-full object-cover" />
      {video && !videoFailed && (
        <video ref={videoRef} src={video} poster={image} autoPlay muted loop playsInline preload="metadata" aria-hidden="true" onPlaying={() => setPlaying(true)} onPause={() => setPlaying(false)} onError={() => { setVideoFailed(true); setPlaying(false) }} className={`absolute inset-0 -z-10 h-full w-full object-cover ${playing ? "opacity-100" : "opacity-0"}`} />
      )}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-green-950/80 via-green-950/60 to-black/50" />
      {video && !videoFailed && (
        <button onClick={toggleVideo} aria-label={playing ? "Pause hero video" : "Play hero video"} className="absolute right-4 top-5 rounded-full bg-white/90 p-3 text-green-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </button>
      )}
      <div className="mx-auto max-w-6xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:py-32">
        <VizagLogo className="mx-auto mb-8 w-36 shadow-2xl sm:w-44" />
        <p className="mb-6 text-sm font-medium tracking-widest text-green-200">Organized by {event.organizer}</p>
        <h1 className="text-4xl font-bold leading-tight sm:text-5xl lg:text-7xl">{event.name}</h1>
        <p className="mt-5 text-2xl font-semibold text-yellow-300 sm:text-3xl">{event.edition}</p>
        <p className="mt-8 text-lg font-semibold sm:text-xl"><time dateTime={event.isoDate}>{event.date}</time> | <a href={event.venueUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-yellow-300">{event.venue}</a></p>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-green-100 sm:text-lg">{event.description}</p>
        <div className="mt-9 flex flex-col flex-wrap justify-center gap-4 sm:flex-row">
          <button type="button" onClick={onAgendaClick} aria-haspopup="dialog" className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-yellow-300 px-6 py-3 font-semibold text-green-950 shadow-lg hover:bg-yellow-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">View Agenda</button>
          <a href="#sessions" onClick={onPresentationsClick ? (e) => { e.preventDefault(); onPresentationsClick() } : undefined} className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-semibold text-white shadow-lg hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">Explore Presentations <ArrowRight className="h-4 w-4" /></a>
          <a href="#previous-editions" className="rounded-xl border border-white/40 bg-white/10 px-6 py-3 font-semibold hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">Explore Previous Editions</a>
        </div>
      </div>
    </section>
  )
}
